import { useState, useEffect } from "react";
import { Sidebar, SidebarContent, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import ProductsPage from "@/components/ProductsPage";
import CustomFormsPage from "@/components/CustomFormsPage";
import FeedbackPage from "@/components/FeedbackPage";
import InsightsPage from "@/components/InsightsPage";
import IntegrationPage from "@/components/IntegrationPage";
import TeamPage from "@/components/TeamPage";
import WelcomeScreen from "@/components/WelcomeScreen";
import { Button } from "@/components/ui/button";
import { LogOut, Building2, User } from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { getOnboardingStatus, generateSampleData, completeOnboarding } from "@/services/api";

// Single source of truth for the human-readable page title shown in the
// top bar. Keys must match the `id` values used by AppSidebar's nav items.
const PAGE_TITLES: Record<string, string> = {
  feedback: "Feedback",
  insights: "Insights",
  "custom-forms": "Custom Forms",
  products: "Products",
  integration: "Integration",
  team: "Team",
};

const Dashboard = () => {
  const [currentPage, setCurrentPage] = useState("feedback");
  const [showWelcome, setShowWelcome] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { appUser, company } = useAuth();
  const pageTitle = PAGE_TITLES[currentPage] ?? "";

  // Check onboarding status on mount
  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        // Check if user dismissed welcome for this session
        const dismissedThisSession = sessionStorage.getItem('welcomeScreenDismissed') === 'true';
        
        if (dismissedThisSession) {
          setShowWelcome(false);
          setIsCheckingOnboarding(false);
          return;
        }

        const status = await getOnboardingStatus();
        setShowWelcome(status.shouldShowWelcome);
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // Don't show welcome if we can't check status
        setShowWelcome(false);
      } finally {
        setIsCheckingOnboarding(false);
      }
    };

    checkOnboarding();
  }, []);

  const handleLoadSampleData = async () => {
    try {
      const result = await generateSampleData();
      toast({
        title: "Sample data generated!",
        description: `Created ${result.data.products.length} products with ${result.data.feedbackCount} feedback entries.`,
      });
      setShowWelcome(false);
      setCurrentPage("feedback"); // Navigate to feedback page
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate sample data",
        variant: "destructive",
      });
    }
  };

  const handleStartFresh = async () => {
    try {
      await completeOnboarding();
      toast({
        title: "Welcome!",
        description: "Let's start by creating your first product.",
      });
      setShowWelcome(false);
      setCurrentPage("products"); // Navigate to products page
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to complete onboarding. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSkipWelcome = () => {
    // "Maybe Later" - just dismiss for this session, don't mark as complete
    // User will see welcome screen again on next login if they still have 0 products
    sessionStorage.setItem('welcomeScreenDismissed', 'true');
    setShowWelcome(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
      navigate("/login");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show welcome screen if needed
  if (showWelcome && !isCheckingOnboarding) {
    return (
      <WelcomeScreen
        onLoadSampleData={handleLoadSampleData}
        onStartFresh={handleStartFresh}
        onSkip={handleSkipWelcome}
      />
    );
  }

  // Don't render dashboard until we've checked onboarding status
  if (isCheckingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar currentPage={currentPage} setCurrentPage={setCurrentPage} />
        <main className="flex-1 flex flex-col">
          <header className="border-b bg-card p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <SidebarTrigger className="lg:hidden" />
                <h1 className="text-2xl font-bold text-foreground truncate">
                  {pageTitle}
                </h1>
              </div>
              <div className="flex items-center gap-3">
                {(appUser || company) && (
                  <div className="flex flex-col items-end text-sm leading-tight">
                    {appUser && (
                      <div className="flex items-center gap-2 text-foreground">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{appUser.fullName}</span>
                        <span className="px-2 py-0.5 bg-secondary rounded text-xs">
                          {appUser.role}
                        </span>
                      </div>
                    )}
                    {company && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                        <Building2 className="h-3.5 w-3.5" />
                        <span>{company.name}</span>
                      </div>
                    )}
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </header>
          <div className="flex-1 p-6">
            <div style={{ display: currentPage === 'products' ? 'block' : 'none' }}>
              <ProductsPage />
            </div>
            <div style={{ display: currentPage === 'custom-forms' ? 'block' : 'none' }}>
              <CustomFormsPage />
            </div>
            <div style={{ display: currentPage === 'feedback' ? 'block' : 'none' }}>
              <FeedbackPage />
            </div>
            <div style={{ display: currentPage === 'insights' ? 'block' : 'none' }}>
              <InsightsPage />
            </div>
            <div style={{ display: currentPage === 'integration' ? 'block' : 'none' }}>
              <IntegrationPage />
            </div>
            {currentPage === 'team' && (
              <div>
                <TeamPage />
              </div>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Dashboard;
